/**
 * Drive Resilience — appDataFolder operations
 *
 * Read/write system files in Google Drive's appDataFolder.
 * This space is invisible to the user and exclusive to the Bigwa app.
 *
 * SEPARATE from googleDrive/core.ts — the two systems do not mix.
 * core.ts handles user-visible Drive; this handles appDataFolder only.
 */

import { googleAuth, getTokenOrRefresh } from './googleAuth';
import { DRIVE_API_BASE } from './googleDrive/core';
import { retryOrThrow, RETRY_PRESETS } from '../core/utils/retry';
import { RateLimitError, HttpError } from '../core/errors/NetworkError';
import { logger } from '../utils/logger';

// ============================================================================
// RETRY CONFIG — 429-only (CLAUDE.md rule)
// ============================================================================

const shouldRetryAppData = (error: Error): boolean => {
  if (error instanceof RateLimitError) return true;
  if (error instanceof HttpError && error.statusCode === 429) return true;
  return false;
};

const APPDATA_RETRY_OPTIONS = {
  ...RETRY_PRESETS.CONSERVATIVE,
  shouldRetry: shouldRetryAppData,
};

// ============================================================================
// SCOPE CHECK
// ============================================================================

let scopeCheckResult: boolean | null = null;

/**
 * Check if the current token has drive.appdata scope.
 * Probes with a minimal files.list call. Result cached for the session.
 */
export async function hasAppDataScope(): Promise<boolean> {
  if (scopeCheckResult !== null) return scopeCheckResult;

  const token = googleAuth.getAccessToken();
  if (!token) {
    scopeCheckResult = false;
    return false;
  }

  try {
    const url = `${DRIVE_API_BASE}/files?spaces=appDataFolder&pageSize=1&fields=files(id)`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 403 || response.status === 401) {
      scopeCheckResult = false;
      return false;
    }

    if (!response.ok) {
      // Unexpected error — don't cache, let caller decide
      logger.warn('DriveResilience', `Scope check returned ${response.status}`);
      scopeCheckResult = false;
      return false;
    }

    scopeCheckResult = true;
    return true;
  } catch (error) {
    logger.warn('DriveResilience', 'Scope check failed:', error);
    scopeCheckResult = false;
    return false;
  }
}

// ============================================================================
// READ SYSTEM FILE
// ============================================================================

/**
 * Read a JSON file from appDataFolder by name.
 * Returns parsed content or null if not found.
 */
export async function readSystemFile(filename: string): Promise<unknown | null> {
  return retryOrThrow(async () => {
    const token = await getToken();

    // 1. Find file by name
    const query = `name='${filename}' and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?spaces=appDataFolder&q=${encodeURIComponent(query)}&fields=files(id,name)&pageSize=1`;

    const searchResponse = await fetch(searchUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!searchResponse.ok) {
      throwHttpError(searchResponse.status, `Failed to search appDataFolder for ${filename}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    // 2. Download content
    const fileId = searchData.files[0].id;
    const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;

    const downloadResponse = await fetch(downloadUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!downloadResponse.ok) {
      throwHttpError(downloadResponse.status, `Failed to read ${filename} from appDataFolder`);
    }

    const text = await downloadResponse.text();
    try {
      return JSON.parse(text);
    } catch (err) {
      logger.error('DriveResilience', `JSON.parse failed for ${filename}:`, err);
      return null;
    }
  }, APPDATA_RETRY_OPTIONS);
}

// ============================================================================
// WRITE SYSTEM FILE
// ============================================================================

/**
 * Write a JSON file to appDataFolder (create or update).
 * Returns the file ID.
 */
export async function writeSystemFile(filename: string, data: unknown): Promise<string> {
  return retryOrThrow(async () => {
    const token = await getToken();

    // 1. Check if file already exists
    const query = `name='${filename}' and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?spaces=appDataFolder&q=${encodeURIComponent(query)}&fields=files(id)&pageSize=1`;

    const searchResponse = await fetch(searchUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!searchResponse.ok) {
      throwHttpError(searchResponse.status, `Failed to search appDataFolder for ${filename}`);
    }

    const searchData = await searchResponse.json();
    const existingId = searchData.files?.[0]?.id;
    const content = JSON.stringify(data, null, 2);

    if (existingId) {
      // 2a. Update existing file
      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${existingId}?uploadType=media`;
      const updateResponse = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: content,
      });

      if (!updateResponse.ok) {
        throwHttpError(updateResponse.status, `Failed to update ${filename} in appDataFolder`);
      }

      return existingId;
    } else {
      // 2b. Create new file with multipart upload
      const metadata = {
        name: filename,
        parents: ['appDataFolder'],
        mimeType: 'application/json',
      };

      const boundary = '---bigwa_appdata_boundary';
      const body = [
        `--${boundary}`,
        'Content-Type: application/json; charset=UTF-8',
        '',
        JSON.stringify(metadata),
        `--${boundary}`,
        'Content-Type: application/json',
        '',
        content,
        `--${boundary}--`,
      ].join('\r\n');

      const createResponse = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': `multipart/related; boundary=${boundary}`,
          },
          body,
        }
      );

      if (!createResponse.ok) {
        throwHttpError(createResponse.status, `Failed to create ${filename} in appDataFolder`);
      }

      const result = await createResponse.json();
      return result.id;
    }
  }, APPDATA_RETRY_OPTIONS);
}

// ============================================================================
// RESET (for testing/logout)
// ============================================================================

/**
 * Clear cached scope check result. Called on logout/reset.
 */
export function resetScopeCache(): void {
  scopeCheckResult = null;
}

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

async function getToken(): Promise<string> {
  return getTokenOrRefresh();
}

function throwHttpError(status: number, message: string): never {
  if (status === 429) {
    throw new RateLimitError(message);
  }
  throw new HttpError(status, message);
}
